import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { User, LoginForm, AuthResponse } from "@/types";
import apiClient from "@/api/client";
import Cookies from "js-cookie";
import router from "@/router";

export const useAuthStore = defineStore("auth", () => {
    // 状态
    const user = ref<User | null>(null);
    const accessToken = ref<string | null>(Cookies.get("access_token"));
    const refreshToken = ref<string | null>(Cookies.get("refresh_token"));
    const isLoading = ref(false);

    // 计算属性
    const isLoggedIn = computed(() => !!accessToken.value);
    const isAdmin = computed(() => user.value?.role === "admin");
    const isLabUser = computed(() => user.value?.role === "lab_user");
    const isHospitalUser = computed(() => user.value?.role === "hospital_user");
    const currentHospitalId = computed(() => user.value?.hospital_id || null);

    // 保存 token 到 Cookie
    const saveTokens = (access: string, refresh: string) => {
        const expiresDays = 7; // refresh token 7天过期
        Cookies.set("access_token", access, { expires: 1 }); // access token 1天过期
        Cookies.set("refresh_token", refresh, { expires: expiresDays });
        accessToken.value = access;
        refreshToken.value = refresh;
    };

    // 清除 token
    const clearTokens = () => {
        Cookies.remove("access_token");
        Cookies.remove("refresh_token");
        accessToken.value = null;
        refreshToken.value = null;
    };

    // 登录
    const login = async (loginForm: LoginForm) => {
        try {
            isLoading.value = true;
            const response = await apiClient.post<AuthResponse>("/api/auth/login", loginForm);
            // 响应已经被拦截器解包，直接从response.data获取数据
            const { access_token, refresh_token, user_info: userData } = response.data;

            saveTokens(access_token, refresh_token);
            user.value = userData;

            return userData;
        } catch (error) {
            clearTokens();
            user.value = null;
            throw error;
        } finally {
            isLoading.value = false;
        }
    };

    // 登出
    const logout = async () => {
        try {
            if (accessToken.value) {
                await apiClient.post("/api/auth/logout");
            }
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            clearTokens();
            user.value = null;
            router.push("/login");
        }
    };

    // 获取当前用户信息
    const fetchCurrentUser = async () => {
        try {
            const response = await apiClient.get<User>("/api/auth/me");
            user.value = response.data;
            return user.value;
        } catch (error) {
            clearTokens();
            user.value = null;
            throw error;
        }
    };

    // 刷新 access token
    const refreshAccessToken = async () => {
        if (!refreshToken.value) {
            throw new Error("No refresh token available");
        }

        try {
            const response = await apiClient.post<{ access_token: string }>("/api/auth/refresh", {
                refresh_token: refreshToken.value,
            });

            const { access_token } = response.data;
            saveTokens(access_token, refreshToken.value!);
            return access_token;
        } catch (error) {
            clearTokens();
            user.value = null;
            throw error;
        }
    };

    // 修改密码
    const changePassword = async (oldPassword: string, newPassword: string) => {
        await apiClient.post("/api/auth/change-password", {
            old_password: oldPassword,
            new_password: newPassword,
        });
    };

    // 检查认证状态
    const checkAuth = async () => {
        if (!accessToken.value) {
            return;
        }

        try {
            await fetchCurrentUser();
        } catch (error) {
            console.error("Auth check failed:", error);
            // 只有在token无效时才清除，网络错误等情况不清除
            if (error.response?.status === 401) {
                clearTokens();
                user.value = null;
            }
        }
    };

    return {
        // 状态
        user,
        accessToken,
        refreshToken,
        isLoading,

        // 计算属性
        isLoggedIn,
        isAdmin,
        isLabUser,
        isHospitalUser,
        currentHospitalId,

        // 方法
        login,
        logout,
        fetchCurrentUser,
        refreshAccessToken,
        changePassword,
        checkAuth,
        saveTokens,
        clearTokens,
    };
});
